// Site switcher — the header's "Test site — Jaipur ▾" control and the shared
// site list behind it. Desktop: a Radix popover (Escape, outside click and
// focus return for free) holding a cmdk list (type-to-filter, arrow keys,
// Enter). Phones: the trigger opens the context's bottom sheet instead, which
// renders the same `SiteList`. Product-neutral: no product colour, numbers in
// the numeric face, one `Check` on the current row, a connector StatusDot per
// site when heartbeat rows are already cached.

import { useState, type ReactNode } from "react";
import * as RPopover from "@radix-ui/react-popover";
import { Command } from "cmdk";
import { useQuery } from "urql";
import { Check, ChevronDown, Layers, MapPin, Plus, Search } from "lucide-react";

import { Skeleton, StatusDot } from "@/app/console/ui";
import { useIsPhoneWidth } from "@/app/console/useViewport";
import { ConnectorStatusDocument } from "@/app/queriesF2b";
import { connectorFor } from "@/app/site/connectorState";
import { fleetCapacity, siteCapacity, useSite, type SiteId } from "@/app/site/SiteContext";

const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60";

/** Header-style label: the site name, or "All sites · n sites". */
export function useSiteLabel(): { label: string; detail: string | null; loading: boolean } {
  const { sites, loading, currentSite } = useSite();
  if (loading) return { label: "Loading sites…", detail: null, loading: true };
  if (currentSite) return { label: currentSite.name, detail: currentSite.locationState || null, loading: false };
  if (sites.length === 0) return { label: "No sites yet", detail: null, loading: false };
  return { label: "All sites", detail: `${sites.length} site${sites.length === 1 ? "" : "s"}`, loading: false };
}

// ---------------------------------------------------------------------------
// Trigger + popover
// ---------------------------------------------------------------------------

export function SiteSwitcher({
  variant = "header",
  className,
}: {
  /** `header`: compact bordered button (36 / 44 px). `inline`: a text control for the launcher's site line. */
  variant?: "header" | "inline";
  className?: string;
}) {
  const { openPicker } = useSite();
  const { label, detail, loading } = useSiteLabel();
  const phone = useIsPhoneWidth();
  const [open, setOpen] = useState(false);

  const text = variant === "header" ? label : detail && label === "All sites" ? `${label} · ${detail}` : label;
  const triggerClass =
    variant === "header"
      ? `group inline-flex h-9 max-w-[220px] min-w-0 items-center gap-2 rounded-control border border-line px-2.5 text-sm text-text-1 transition-colors duration-fast hover:border-line-strong hover:bg-white/[0.04] coarse:h-11 ${FOCUS} ${className ?? ""}`
      : `group inline-flex h-7 max-w-full min-w-0 items-center gap-1 rounded-control px-1 text-sm leading-[22px] text-text-2 transition-colors duration-fast hover:bg-white/[0.04] hover:text-text-1 coarse:h-11 ${FOCUS} ${className ?? ""}`;

  const trigger = (onClick?: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className={triggerClass}
      aria-label={`Current site: ${text}. Switch site`}
      aria-haspopup="listbox"
      aria-expanded={open}
      data-testid="site-switcher"
    >
      {variant === "header" && <MapPin className="h-4 w-4 shrink-0 text-text-3 transition-colors duration-fast group-hover:text-text-2" aria-hidden="true" />}
      {loading ? <Skeleton width={110} height={12} className="!inline-block" /> : <span className="min-w-0 truncate">{text}</span>}
      <ChevronDown className="h-3.5 w-3.5 shrink-0 text-text-3" aria-hidden="true" />
    </button>
  );

  // Below `sm` the list is a bottom sheet owned by the provider.
  if (phone) return trigger(openPicker);

  return (
    <RPopover.Root open={open} onOpenChange={setOpen}>
      <RPopover.Trigger asChild>{trigger()}</RPopover.Trigger>
      <RPopover.Portal>
        <RPopover.Content
          align="start"
          sideOffset={6}
          collisionPadding={8}
          className="z-popover w-[360px] max-w-[calc(100vw-1rem)] overflow-hidden rounded-card border border-line-strong bg-surface-3 p-0 shadow-[0_16px_48px_rgba(0,0,0,0.5)]"
        >
          <SiteList onDone={() => setOpen(false)} />
        </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  );
}

// ---------------------------------------------------------------------------
// The list — shared by the popover and the phone sheet
// ---------------------------------------------------------------------------

const ROW =
  "group flex min-h-10 cursor-pointer items-center gap-2.5 rounded-control px-2 py-1.5 text-sm text-text-2 outline-none data-[selected=true]:bg-white/[0.06] data-[selected=true]:text-text-1 coarse:min-h-11";

export function SiteList({ onDone, autoFocus = true }: { onDone: () => void; autoFocus?: boolean }) {
  const { sites, loading, error, currentSiteId, setCurrentSite, canManageSites, openAddSite } = useSite();
  // Connector heartbeats are already cached by the Sites table / Connectors
  // page; cache-first means this costs nothing when they are, and a single
  // request otherwise. Dots are omitted entirely when nothing is enrolled.
  const [{ data: connData }] = useQuery({ query: ConnectorStatusDocument, requestPolicy: "cache-first" });
  const connectors = connData?.connectorStatus ?? [];
  const showDots = connectors.length > 0;

  const pick = (id: SiteId) => {
    setCurrentSite(id);
    onDone();
  };

  return (
    <Command loop label="Switch site" className="flex max-h-[min(440px,70vh)] flex-col">
      <div className="flex items-center gap-2 border-b border-line px-3">
        <Search className="h-3.5 w-3.5 shrink-0 text-text-3" aria-hidden="true" />
        <Command.Input
          autoFocus={autoFocus}
          placeholder="Search sites…"
          aria-label="Search sites"
          className="h-10 w-full bg-transparent text-sm text-text-1 placeholder-text-3 outline-none coarse:h-11 coarse:text-base"
        />
      </div>
      <Command.List className="min-h-0 flex-1 overflow-y-auto">
        <Command.Empty className="py-6 text-center text-sm text-text-3">No sites match.</Command.Empty>
        <Command.Group className="p-1">
          <Command.Item value="all sites" onSelect={() => pick("all")} className={ROW} aria-current={currentSiteId === "all" ? "true" : undefined}>
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] border border-line bg-surface-2 text-text-3" aria-hidden="true">
              <Layers className="h-3.5 w-3.5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-text-1">All sites</span>
              <span className="block truncate text-xs leading-4 text-text-3">
                {loading ? "Loading…" : `${sites.length} site${sites.length === 1 ? "" : "s"} · fleet totals`}
              </span>
            </span>
            {sites.length > 0 && <span className="shrink-0 font-mono text-xs tabular-nums text-text-2">{fleetCapacity(sites)}</span>}
            <Mark on={currentSiteId === "all"} />
          </Command.Item>

          {sites.map((s) => {
            const conn = showDots ? connectorFor(s, connectors) : null;
            const current = currentSiteId === s.id;
            return (
              <Command.Item key={s.id} value={`${s.name} ${s.locationState} ${s.id}`} onSelect={() => pick(s.id)} className={ROW} aria-current={current ? "true" : undefined}>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center" aria-hidden="true">
                  {conn ? <StatusDot tone={conn.tone} size="sm" pulse={conn.tone === "ok"} title={`Connector ${conn.label}`} /> : <MapPin className="h-3.5 w-3.5 text-text-3" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-text-1">{s.name}</span>
                  <span className="block truncate text-xs leading-4 text-text-3">{s.locationState || "—"}</span>
                </span>
                <span className="shrink-0 font-mono text-xs tabular-nums text-text-2">{siteCapacity(s)}</span>
                <Mark on={current} />
              </Command.Item>
            );
          })}
        </Command.Group>
        {error && <p className="px-3 pb-2 text-xs text-danger">Could not load sites: {error}</p>}

        {canManageSites && (
          <Command.Item
            value="add site"
            forceMount
            onSelect={() => {
              onDone();
              openAddSite();
            }}
            className={`${ROW} sticky bottom-0 mx-0 rounded-none border-t border-line bg-surface-3 px-3 text-text-1 data-[selected=true]:bg-white/[0.06]`}
            data-testid="site-add-row"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] border border-line bg-surface-2 text-text-2" aria-hidden="true">
              <Plus className="h-3.5 w-3.5" />
            </span>
            <span className="flex-1 font-medium">Add site</span>
          </Command.Item>
        )}
      </Command.List>
    </Command>
  );
}

function Mark({ on }: { on: boolean }) {
  return on ? <Check className="h-3.5 w-3.5 shrink-0 text-text-1" aria-hidden="true" /> : <span className="w-3.5 shrink-0" aria-hidden="true" />;
}

// ---------------------------------------------------------------------------
// Phone rows — the mobile nav drawer header and the tab bar's "More" sheet
// ---------------------------------------------------------------------------

/** A 44 px "Site · <name> ▾" row; `onOpen` should close the host and open the picker. */
export function SiteNavRow({ onOpen, trailing }: { onOpen: () => void; trailing?: ReactNode }) {
  const { label, detail, loading } = useSiteLabel();
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`flex h-11 w-full items-center gap-3 rounded-control px-2 text-left transition-colors duration-fast hover:bg-white/[0.04] ${FOCUS}`}
      aria-label={`Current site: ${label}. Switch site`}
      data-testid="site-nav-row"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] border border-line bg-surface-2 text-text-3" aria-hidden="true">
        <MapPin className="h-4 w-4" strokeWidth={1.75} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="pb-micro block text-text-3">Site</span>
        {loading ? <Skeleton width={120} height={12} className="mt-1" /> : <span className="block truncate text-sm font-medium leading-5 text-text-1">{label}{detail && label === "All sites" ? ` · ${detail}` : ""}</span>}
      </span>
      {trailing ?? <ChevronDown className="h-4 w-4 shrink-0 text-text-3" aria-hidden="true" />}
    </button>
  );
}
